import React, { useEffect, useRef, useState } from 'react';
import { chatFocus } from './chat-theme';
import { HiArrowUp } from 'react-icons/hi2';

const MAX_CHARS = 20000;

/** Message box: Enter sends, Shift+Enter adds a line. No voice or attachments yet. */
export default function Composer({ onSend, disabled, placeholder = 'Message…', autoFocus, initialText, onInitialTextUsed, value, onChange }) {
    const [localText, setLocalText] = useState('');
    const text = value ?? localText;
    const setText = onChange || setLocalText;
    const ref = useRef(null);

    // A handoff from another scope (after a ROUTE): pre-fill it for the builder to review and send.
    useEffect(() => {
        if (typeof initialText === 'string' && initialText) {
            setText(initialText);
            if (onInitialTextUsed) onInitialTextUsed();
            ref.current?.focus();
        }
    }, [initialText, onInitialTextUsed, setText]);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
    }, [text]);

    useEffect(() => {
        if (autoFocus) ref.current?.focus();
    }, [autoFocus]);

    const submit = () => {
        if (disabled || !text.trim()) return;
        onSend(text);
        setText('');
    };

    return (
        <form
            onSubmit={event => { event.preventDefault(); submit(); }}
            className="flex items-end gap-2 rounded-2xl border border-slate-300 bg-white py-2.5 pl-4 pr-2.5 shadow-[0_4px_20px_-6px_rgba(15,23,42,0.18)] transition-shadow focus-within:border-lime-600 focus-within:ring-2 focus-within:ring-lime-500/20 dark:focus-within:border-brand-lime"
        >
            <label htmlFor="copilot-message" className="sr-only">Message the copilot</label>
            <textarea
                id="copilot-message"
                ref={ref}
                rows={1}
                value={text}
                maxLength={MAX_CHARS}
                placeholder={placeholder}
                onChange={event => setText(event.target.value)}
                onKeyDown={event => {
                    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
                        event.preventDefault();
                        submit();
                    }
                }}
                className="max-h-40 flex-1 resize-none border-0 bg-transparent p-0 py-1.5 text-[0.925rem] text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0"
            />
            <button
                type="submit"
                disabled={disabled || !text.trim()}
                aria-label="Send"
                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-brand-lime text-gray-950 transition-colors hover:bg-lime-300 disabled:bg-gray-200 disabled:text-gray-500 ${chatFocus}`}
            >
                <HiArrowUp className="h-4 w-4" />
            </button>
        </form>
    );
}
